import React, { useMemo } from 'react';
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import {
    Button, FormFieldInputNew, Modal, FormFieldSelect,
    ModalContent, ModalFooter, ModalHeader, Layout, useForm,Text
} from "@netapp/bxp-design-system-react";
import styles from "./GTCDataCollectionPage.module.scss"
import countriesList from "./countriesList";
import { useTranslation } from 'react-i18next';
import { appComponents as i18nNamespace } from '../../../i18n/namespace';


const initialData = {
    name: undefined,
    company: undefined,
    country: undefined,
    businessAddress: undefined,
    city: undefined,
    state: undefined,
    postalCode: undefined,
    phone: undefined
};

const getschemaObject  = t => {
    return {
    country: yup.string().required(t("country_required")),
    businessAddress: yup.string()
        .required(t("business_address_required"))
        .min(3, t("min_characters"))
        .max(400, t("max_characters"))
        .matches(/^[a-zA-Z0-9,.-][ a-zA-Z0-9,.-]{2,399}$/, t("match_business_address")),
    city: yup.string()
        .required(t("city_required"))
        .min(2, t("city_min_characters"))
        .max(255, t("city_max_characters"))
        .matches(/^[a-zA-Z.-][ a-zA-Z.-]{1,254}$/, t("match_city_address")),
    state: yup.string()
        .nullable(true)
        .notRequired()
        .max(255, t("city_max_characters"))
        .matches(/^[a-zA-Z.-][ a-zA-Z.-]{1,254}$/, t("match_state_address"))
        .transform((value, originalValue) => _.isEmpty(originalValue) ? null : value),
    postalCode: yup.string()
        .nullable(true)
        .notRequired()
        .max(15, t("postalcode_max_characters"))
        .matches(/^[A-Za-z0-9-][ A-Za-z0-9-]{3,15}$/, t("postalcode_match"))
        .transform((value, originalValue) => _.isEmpty(originalValue) ? null : value),
    phone: yup.string()
        .nullable(true)
        .notRequired()
        .max(19, t("phonenumber_max_characters"))
        .matches(/^\+[0-9]{1,3}\.[0-9]{4,14}$/, t("phonenumber_match"))
        .transform((value, originalValue) => _.isEmpty(originalValue) ? null : value)
}
}

const GTCDataCollectionPage = () => {
    const { email, userId } = useSelector(state => state.auth);
    const { t } = useTranslation(i18nNamespace);
    const schemaObject = getschemaObject(t);
    const { isGtcDataSubmitting, gtcSaveError, userName, userCompany } = useSelector(state => state.app);

    if (userName) {
        _.set(initialData, 'name', userName);
    }
    if (userCompany) {
        _.set(initialData, 'company', userCompany);
    }

    const schemaValidator = useMemo(() => {
        if (!userName) {
            schemaObject['name'] = yup.string()
                .required(t("name_required"))
                .min(1)
                .max(255, t("fullname_max_characters"))
                .matches(/^[a-zA-Z][ a-zA-Z]{0,254}$/, t("match_fullname"));
        }

        if (!userCompany) {
            schemaObject['company'] = yup.string()
                .required(t("company_required"))
                .min(2, t("company_min_characters"))
                .max(255, t("company_max_characters"))
                .matches(/^[a-zA-Z][ a-zA-Z]{1,254}$/, t("match_company_name"));
        }

        return yup.object().shape(schemaObject);
    }, [userName, userCompany, t]);
    
    const userSignUpForm = useForm(initialData, schemaValidator);
    const { handleFormSubmit } = userSignUpForm;

    const dispatch = useDispatch();

    const handleUserFormSubmit = () => {
        const formData = {
            email: email.trim(),
            auth0Id: userId,
            name: userSignUpForm.formState.name.trim(),
            company: userSignUpForm.formState.company.trim(),
            country: userSignUpForm.formState.country.value,
            address: userSignUpForm.formState.businessAddress.trim(),
            city: userSignUpForm.formState.city.trim()
        };

        if (userSignUpForm.formState.postalCode?.trim()) {
            formData['postalCode'] = userSignUpForm.formState.postalCode.trim();
        }
        if (userSignUpForm.formState.phone?.trim()) {
            formData['phone'] = userSignUpForm.formState.phone.trim();
        }
        if (userSignUpForm.formState.state?.trim()) {
            formData['state'] = userSignUpForm.formState.state.trim();
        }

        dispatch({
            type: "APP:SAVE-GTC-DATA",
            payload: { 
                userId,
                formData
             }
        });
    };

    return <Modal className={styles.base}>
        <ModalHeader>
            {t("welcome_text")}
        </ModalHeader>
        <ModalContent>
        <Text level="14" className={styles['modal-sub-title']}>{t("form")}</Text>
            <Layout.Grid>
                <Layout.GridItem lg={6}>
                    <FormFieldInputNew
                        form={userSignUpForm}
                        name="name"
                        label={t("full_name")}
                        maxChars={256}
                        autoComplete="off"
                        style={{ marginBottom: 32 }}
                        showError={userSignUpForm.dirty.name && userSignUpForm.formErrors.name}
                        disabled={userName ? true : false}
                    />
                    <FormFieldSelect
                        form={userSignUpForm}
                        name="country"
                        label={t("country")}
                        placeholder={t("select_country")}
                        isSearchable={true}
                        options={countriesList}
                        style={{ marginBottom: 32 }}
                        showError={userSignUpForm.dirty.country && userSignUpForm.formErrors.country}
                    />
                    <FormFieldInputNew
                        form={userSignUpForm}
                        name="city"
                        label={t("city")}
                        maxChars={256}
                        style={{ marginBottom: 32 }}
                        showError={userSignUpForm.dirty.city && userSignUpForm.formErrors.city}
                    />
                    <FormFieldInputNew
                        form={userSignUpForm}
                        isOptional={true}
                        name="postalCode"
                        label={t("postal_code")}
                        maxChars={256}
                        showError={userSignUpForm.dirty.postalCode && userSignUpForm.formErrors.postalCode}
                    />
                </Layout.GridItem>
                <Layout.GridItem lg={6}>
                    <FormFieldInputNew
                        form={userSignUpForm}
                        name="company"
                        label={t("company")}
                        maxChars={256}
                        style={{ marginBottom: 32 }}
                        showError={userSignUpForm.dirty.emailId && userSignUpForm.formErrors.emailId}
                        disabled={userCompany ? true : false}
                    />
                    <FormFieldInputNew
                        label={t("business_address")}
                        form={userSignUpForm}
                        name="businessAddress"
                        style={{ marginBottom: 32 }}
                        showError={userSignUpForm.dirty.businessAddress && userSignUpForm.formErrors.businessAddress}
                    />
                    <FormFieldInputNew
                        form={userSignUpForm}
                        isOptional={true}
                        name="state"
                        label={t("state")}
                        maxChars={256}
                        style={{ marginBottom: 32 }}
                        showError={userSignUpForm.dirty.state && userSignUpForm.formErrors.state}
                    />
                    <FormFieldInputNew
                        form={userSignUpForm}
                        isOptional={true}
                        name="phone"
                        label={t("phone")}
                        placeholder={t("phone_placeholder")}
                        maxChars={256}
                        showError={userSignUpForm.dirty.phone && userSignUpForm.formErrors.phone}
                    />
                </Layout.GridItem>
            </Layout.Grid>
        </ModalContent>
        <ModalFooter error={gtcSaveError ? gtcSaveError : undefined}>
            <Button submit isSubmitting={isGtcDataSubmitting} onClick={handleFormSubmit(handleUserFormSubmit)}>{t("save")}</Button>
        </ModalFooter>
    </Modal>
}

export default GTCDataCollectionPage;